import { useMemo, useState } from 'react';
import {
  CapaPcbServiceType,
  CapaQuotationChatRoomStatus,
  CapaQuotationPartnerStatus,
  CapaQuotationServiceType,
  CapaQuotationUserVerificationType,
  CapaRfqStatus,
  FetchQuotationUserRfqDetailQuery,
  useChangeChatRoomStatusOpenMutation,
  useFetchMyQuotationUserQuery,
  useFetchMyRfqQuotationsQuery,
  useRegisterRequestRfqQuotationMutation,
  useFetchMyReceivedQuotationsQuery,
  CapaPurchaseOrderStatus
} from '@generated/graphql';
import useActiveTab, { TabState } from './useActiveTab';
import { QuotationType } from '@modules/dashboard';
import FixedBottomCTA from '../FixedBottomCTA';
import LoadingPresenter from '@components/Loading';
import { IButtonProps, IDialogContentsProps } from '../types';
import PlacingAnOrderDialog from '@routes/Dashboard/PlacingAnOrderDialog';
import { useSelector } from '@store';
import OrderedQuotationDetailDialog from '@routes/Dashboard/OrderedQuotationDetailDialog';
import useChat from '@hooks/useChat';
import QuotationDetailPresenter from './QuotationDetailPresenter';
import { VerificationDialog } from '@routes/MyAccount/components/Dialog/RequestActionDialog';

interface Props {
  matches: boolean;
  notIssuedQuotation: boolean;
  quotationType?: QuotationType;

  rfqInfo?: FetchQuotationUserRfqDetailQuery;

  rfqId: string;
  quotationId?: string;
  partnerId?: string;
  chatRoomId?: string;

  onChangePath: (_path: string, callback?: () => void) => void;
  placingAnOrder: () => void;
}

const QuotationDetailContainer = ({
  matches,
  quotationType,
  notIssuedQuotation,
  //
  rfqInfo,
  //
  rfqId,
  quotationId = '',
  partnerId = '',
  chatRoomId,
  //
  onChangePath,
  placingAnOrder
}: Props) => {
  const { data: userInfo } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-first'
  });
  const isNotVerifiedUser =
    userInfo?.fetchMyQuotationUser?.data?.verificationType !==
    CapaQuotationUserVerificationType?.Verified;
  const {
    data: quotationInfoData,
    loading: fetchQuotationsLoading
  } = useFetchMyRfqQuotationsQuery({
    variables: {
      input: {
        rfqId: rfqId,
        quotationPartnerId: partnerId
      }
    },
    fetchPolicy: 'network-only',
    skip: !rfqId || !partnerId || quotationType === 'REQUEST'
  });

  const [changeRoomStatusOpenMutation] = useChangeChatRoomStatusOpenMutation();
  const [registerRequestQuotation] = useRegisterRequestRfqQuotationMutation();
  const {
    data: fetchedMyReceivedQuotations
  } = useFetchMyReceivedQuotationsQuery({
    variables: {
      input: {
        rfqId: rfqId ?? ''
      }
    },
    fetchPolicy: 'network-only'
  });

  const recentPurchaseOrderStatus =
    fetchedMyReceivedQuotations?.fetchMyReceivedQuotations?.data?.[0]
      ?.purchaseOrderStatus;

  const purchaseCondition =
    recentPurchaseOrderStatus === CapaPurchaseOrderStatus.CancelConfirmed ||
    recentPurchaseOrderStatus === CapaPurchaseOrderStatus.RefundConfirmed;

  const requestToRegisterQuotation = async () => {
    if (!rfqId || !partnerId) return;

    await changeRoomStatusOpenMutation({
      variables: {
        input: {
          chatRoomId: chatRoomId ?? '',
          quotationId: quotationId === '' ? null : quotationId
        }
      }
    });

    await registerRequestQuotation({
      variables: {
        input: {
          rfqId,
          partnerId
        }
      }
    });

    onChangePath(`/chat/${chatRoomId}`);
  };

  const [activeTab, setActiveTab] = useActiveTab({ quotationId });
  const [placingAnOrderDialogOpen, setPlacingAnOrderDialogOpen] = useState(
    false
  );
  const [
    orderedQuotationDetailDialogOpen,
    setOrderedQuotationDetailDialogOpen
  ] = useState(false);
  const [openNotVerifiedModal, setOpenNotVerifiedModal] = useState(false);

  const chatRooms =
    rfqInfo?.fetchQuotationUserRfqDetail.data?.chatRoom &&
    rfqInfo?.fetchQuotationUserRfqDetail.data?.chatRoom.length > 0
      ? rfqInfo?.fetchQuotationUserRfqDetail.data?.chatRoom
      : [];

  const onChangeTab = (value: TabState) => setActiveTab(value);

  const quotation =
    quotationInfoData?.fetchMyRfqQuotations.data &&
    quotationInfoData?.fetchMyRfqQuotations.data.length > 0
      ? quotationInfoData.fetchMyRfqQuotations.data
      : undefined;

  const latestQuotation = quotation ? quotation[0] : undefined;

  const chatRoom = chatRooms.find((room) => room.chatRoomId === chatRoomId);

  const roomStatus = chatRoom?.chatRoomStatus;

  const openedPartner =
    CapaQuotationChatRoomStatus.OpenedPartner === roomStatus;

  const handleGoChat = async () => {
    if (!roomStatus || openedPartner) {
      await changeRoomStatusOpenMutation({
        variables: {
          input: {
            chatRoomId: chatRoomId ?? '',
            quotationId: quotationId === '' ? null : quotationId
          }
        }
      }).finally(() => {
        onChangePath(`/chat/${chatRoomId}`);
      });
    } else {
      onChangePath(`/chat/${chatRoomId}`);
    }
  };

  const haveConsulted = quotation ? quotation.length > 1 : false;

  const isDesign =
    latestQuotation?.serviceType === CapaQuotationServiceType.Design ||
    (rfqInfo?.fetchQuotationUserRfqDetail?.data?.serviceType ===
      CapaQuotationServiceType.Pcb &&
      rfqInfo?.fetchQuotationUserRfqDetail?.data?.pcb?.services?.some(
        (service) => service?.serviceType === CapaPcbServiceType.CircuitDesign
      ));

  const isWithdrawn =
    latestQuotation?.supply?.quotationPartnerStatus ===
    CapaQuotationPartnerStatus.Deleted;

  const chatLabel =
    chatRoom?.chatRoomStatus === CapaQuotationChatRoomStatus.OpenedPartner
      ? '채팅 시작하기'
      : '채팅방으로 이동';

  const orderedQuotation = useSelector(
    (state) => state.dashboard.selectedQuotationId?.ordered
  );

  const isPurchaseOrder = useSelector(
    (state) => state.dashboard.selectedQuotationId?.isPurchaseOrder
  );

  const isOnlyIntroduction = useSelector(
    (state) => state.dashboard.selectedQuotationId?.isOnlyIntroduction
  );

  const chatRoomStatus = chatRoom?.chatRoomStatus;

  const isClosedConsulting = chatRoomStatus
    ? [
        CapaQuotationChatRoomStatus.ClosedBeforeConsulting,
        CapaQuotationChatRoomStatus.ClosedClient,
        CapaQuotationChatRoomStatus.ClosedConfirm,
        CapaQuotationChatRoomStatus.ClosedPartner
      ].includes(chatRoomStatus)
    : false;

  const { onInitChatInfo } = useChat();

  const IsAvailableRfq =
    rfqInfo?.fetchQuotationUserRfqDetail.data?.status ===
    CapaRfqStatus.Available;

  const buttons = [
    {
      disabled: !orderedQuotation ? isClosedConsulting : false,
      label:
        !orderedQuotation || purchaseCondition ? '발주하기' : '발주서 보기',
      color: 'default',
      invisibility:
        isWithdrawn ||
        (!orderedQuotation && (isPurchaseOrder || !IsAvailableRfq)),
      variant: 'contained',
      action: () => {
        if (isNotVerifiedUser) {
          return setOpenNotVerifiedModal(true);
        }
        if (orderedQuotation && !purchaseCondition) {
          return setOrderedQuotationDetailDialogOpen((prev) => true);
        }

        if (quotationId && !openedPartner) {
          return placingAnOrder();
        }

        if (placingAnOrderDialogContent) {
          setPlacingAnOrderDialogOpen((prev) => true);
        }
      }
    },
    {
      label: chatLabel,
      color: 'primary',
      variant: 'contained',
      action: handleGoChat
    }
  ] as IButtonProps[];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const placingAnOrderDialogContents = [
    {
      mainContent: `아직 채팅을 통해 견적상담을 하지않았습니다.\n바로 발주하시겠습니까?`,
      subContent: `발주서 내용을 파트너가 확인하고 발주를 거절할 수 있습니다.`,
      button: `발주서 작성하기`,
      action: placingAnOrder
    },
    {
      mainContent: `파트너에게 견적서를 받지 않으셨습니다.\n발주를 하기 위해서는 견적서를 요청하세요. `,
      subContent: `파트너와의 채팅방이 열리며 견적서 요청이 전송됩니다.`,
      button: `견적서 요청하기`,
      action: requestToRegisterQuotation
    },
    {
      mainContent: `발주를 하기위해선 견적서가 필요합니다.\n견적서를 요청하시겠습니까?`,
      subContent: `파트너에게 견적서 요청이 전송됩니다.`,
      button: `견적서 요청하기`,
      action: requestToRegisterQuotation
    }
  ] as IDialogContentsProps[];

  const placingAnOrderDialogContent = useMemo(() => {
    const quotationAndNoConsult = !isOnlyIntroduction && openedPartner;

    const NoQuotaionAndNoConsult = isOnlyIntroduction && openedPartner;

    const NoQuotaionAndConsult = isOnlyIntroduction && !openedPartner;

    if (quotationAndNoConsult) {
      return placingAnOrderDialogContents[0];
    }

    if (NoQuotaionAndNoConsult) {
      return placingAnOrderDialogContents[1];
    }

    if (NoQuotaionAndConsult) {
      return placingAnOrderDialogContents[2];
    }

    return false;
  }, [isOnlyIntroduction, openedPartner, placingAnOrderDialogContents]);

  if (fetchQuotationsLoading) return <LoadingPresenter open />;

  return (
    <>
      {placingAnOrderDialogOpen && placingAnOrderDialogContent && (
        <PlacingAnOrderDialog
          open={placingAnOrderDialogOpen}
          onClose={() => setPlacingAnOrderDialogOpen((prev) => false)}
          contents={placingAnOrderDialogContent}
        />
      )}

      {orderedQuotationDetailDialogOpen && (
        <OrderedQuotationDetailDialog
          open={orderedQuotationDetailDialogOpen}
          onClose={() => {
            onInitChatInfo();
            setOrderedQuotationDetailDialogOpen((prev) => false);
          }}
          rfqId={rfqId}
          partnerId={partnerId}
          chatRoomId={chatRoomId}
        />
      )}

      <VerificationDialog
        open={openNotVerifiedModal}
        onClose={() => setOpenNotVerifiedModal(false)}
      />

      {matches && orderedQuotationDetailDialogOpen ? null : (
        <FixedBottomCTA
          className="chatStart"
          subTitle="CAPA에서 탈퇴한 파트너와는 채팅을 시작할 수 없습니다."
          buttons={buttons}
          matches={matches}
          isWithdrawn={isWithdrawn}
          quotation={latestQuotation?.quotation ?? 0}
          setActiveTab={setActiveTab}
        >
          <QuotationDetailPresenter
            matches={matches}
            isDesign={isDesign ?? false}
            rfqId={rfqId}
            haveConsulted={haveConsulted}
            partnerId={partnerId}
            latestQuotation={latestQuotation}
            isClosedConsulting={isClosedConsulting}
            activeTab={activeTab}
            onChangeTab={onChangeTab}
          />
        </FixedBottomCTA>
      )}
    </>
  );
};

export default QuotationDetailContainer;
